import React, {
  createContext,
  useContext,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { useLocation } from "react-router-dom";
import { analyticsService } from "./analyticsService";

const AnalyticsContext = createContext(null);

export const useAnalyticsContext = () => {
  const context = useContext(AnalyticsContext);
  if (!context) {
    throw new Error(
      "useAnalyticsContext must be used within AnalyticsProvider"
    );
  }
  return context;
};

export function AnalyticsProvider({
  children,
  applicationType: providedApplicationType,
}) {
  const location = useLocation();
  const sessionIdRef = React.useRef(null);

  // Initialize session ID
  useEffect(() => {
    if (!sessionIdRef.current) {
      let sessionId = sessionStorage.getItem("analytics_session_id");

      if (!sessionId) {
        sessionId = `session_${Date.now()}_${Math.random()
          .toString(36)
          .substring(2, 15)}`;
        sessionStorage.setItem("analytics_session_id", sessionId);
      }

      sessionIdRef.current = sessionId;
    }
  }, []);

  // Get base metadata
  const getBaseMetadata = useCallback(() => {
    const segments = location.pathname.split("/").filter(Boolean);
    const inferredApplicationType =
      segments[0] === "ms" && segments[1] ? segments[1] : segments[0] || "dv";
    const applicationType = providedApplicationType || inferredApplicationType;

    return {
      timestamp: new Date().toISOString(),
      sessionId: sessionIdRef.current,
      dmv_uuid: "",
      applicationType,
      pageUrl: window.location.href,
      pathname: location.pathname,
      search: location.search,
      referrer: document.referrer,
      userAgent: navigator.userAgent,
      screenWidth: window.screen.width,
      screenHeight: window.screen.height,
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight,
      },
    };
  }, [location, providedApplicationType]);

  // Track event
  const trackEvent = useCallback(
    async (eventData) => {
      const baseMetadata = getBaseMetadata();
      const metadata = {
        ...baseMetadata,
        ...eventData,
        applicationType:
          eventData?.applicationType || baseMetadata.applicationType,
      };
      await analyticsService.sendEvent(metadata, metadata.applicationType);
    },
    [getBaseMetadata]
  );

  // Global click event handler
  useEffect(() => {
    const handleClick = (event) => {
      // Find the closest element with analytics data
      const element = event.target.closest("[data-analytics]");
      if (!element) return;

      // Extract analytics data from element attributes
      const buttonName =
        element.getAttribute("data-analytics") ||
        element.getAttribute("data-analytics-name") ||
        element.textContent?.trim() ||
        "Unknown Button";

      const action =
        element.getAttribute("data-analytics-action") || buttonName;
      const componentName =
        element.getAttribute("data-analytics-component") || "Unknown";

      // Extract custom data from data-analytics-* attributes
      const customData = {};
      Array.from(element.attributes).forEach((attr) => {
        if (attr.name.startsWith("data-analytics-custom-")) {
          const key = attr.name.replace("data-analytics-custom-", "");
          customData[key] = attr.value;
        }
      });

      // Track the event (non-blocking)
      trackEvent({
        eventType: "BUTTON_CLICK",
        buttonName,
        action,
        componentName,
        elementType: element.tagName.toLowerCase(),
        elementId: element.id || null,
        elementClass: element.className || null,
        customData: Object.keys(customData).length > 0 ? customData : undefined,
      });
    };

    // Add event listener to document
    document.addEventListener("click", handleClick, true);

    // Cleanup
    return () => {
      document.removeEventListener("click", handleClick, true);
    };
  }, [trackEvent]);

  const value = useMemo(
    () => ({
      trackEvent,
      getBaseMetadata,
    }),
    [trackEvent, getBaseMetadata]
  );

  return (
    <AnalyticsContext.Provider value={value}>
      {children}
    </AnalyticsContext.Provider>
  );
}

export const withAnalytics = (Component) =>
  React.forwardRef((props, ref) => {
    const {
      analyticsName,
      analyticsAction,
      analyticsComponent,
      analyticsCustom = {},
      ...restProps
    } = props;

    // Build data attributes
    const analyticsProps = {};

    if (analyticsName) {
      analyticsProps["data-analytics"] = analyticsName;
      analyticsProps["data-analytics-name"] = analyticsName;
    }

    if (analyticsAction) {
      analyticsProps["data-analytics-action"] = analyticsAction;
    }

    if (analyticsComponent) {
      analyticsProps["data-analytics-component"] = analyticsComponent;
    }

    // Add custom data attributes
    Object.keys(analyticsCustom).forEach((key) => {
      analyticsProps[`data-analytics-custom-${key}`] = analyticsCustom[key];
    });

    // eslint-disable-next-line react/jsx-props-no-spreading
    return <Component ref={ref} {...analyticsProps} {...restProps} />;
  });

export const useEventAnalytics = () => {
  const { trackEvent } = useAnalyticsContext();

  const trackButtonClick = useCallback(
    (buttonName, customData = {}) =>
      trackEvent({
        eventType: "BUTTON_CLICK",
        buttonName,
        ...customData,
      }),
    [trackEvent]
  );

  const trackCustomEvent = useCallback(
    (eventType, eventData = {}) =>
      trackEvent({
        eventType,
        ...eventData,
      }),
    [trackEvent]
  );

  return {
    trackButtonClick,
    trackCustomEvent,
    trackEvent,
  };
};
